import type { Session } from '@supabase/supabase-js';

// ── Multi-account session store (#297) ───────────────────────────────────────
//
// `supabase-js` exposes exactly ONE auth session per client (a singleton). To
// support Gmail-style account switching we keep a side-store of every signed-in
// account's token-set + a little profile snapshot, layered over that single
// client. "Switching" means calling `supabase.auth.setSession(...)` with the
// target account's tokens and clearing the React Query cache.
//
// SECURITY: storing N refresh tokens in localStorage is the same blast radius as
// today's single token × N. RLS is unaffected — the server still enforces
// per-JWT access. Only REAL (non-anonymous) accounts are ever stored here;
// anonymous guest sessions are never switchable accounts.
//
// This module is intentionally pure (no React, no supabase calls) so it is
// trivially unit-testable and reused by the hook + provider.

const ACCOUNTS_KEY = 'tk_accounts';
const ACTIVE_USER_ID_KEY = 'tk_active_user_id';

/** A small, display-only snapshot so the switcher can render before a fetch. */
export interface StoredAccountProfile {
  name: string | null;
  avatar: string | null;
}

export interface StoredAccount {
  userId: string;
  email: string | null;
  accessToken: string;
  refreshToken: string;
  profile: StoredAccountProfile;
}

type StoredAccountsMap = Record<string, StoredAccount>;

function safeParse<T>(raw: string | null, fallback: T): T {
  if (!raw) return fallback;
  try {
    return JSON.parse(raw) as T;
  } catch {
    return fallback;
  }
}

/** All stored (real, switchable) accounts, keyed by user id. */
export function getStoredAccounts(): StoredAccountsMap {
  return safeParse<StoredAccountsMap>(localStorage.getItem(ACCOUNTS_KEY), {});
}

/** Stored accounts as an array (stable order: insertion order of the object). */
export function listStoredAccounts(): StoredAccount[] {
  return Object.values(getStoredAccounts());
}

export function getActiveUserId(): string | null {
  return localStorage.getItem(ACTIVE_USER_ID_KEY);
}

export function setActiveUserId(userId: string | null): void {
  if (userId) {
    localStorage.setItem(ACTIVE_USER_ID_KEY, userId);
  } else {
    localStorage.removeItem(ACTIVE_USER_ID_KEY);
  }
}

function writeAccounts(map: StoredAccountsMap): void {
  localStorage.setItem(ACCOUNTS_KEY, JSON.stringify(map));
}

/**
 * Record (or refresh) an account from a live session. No-op for anonymous
 * sessions — guests are never switchable accounts. Also marks it active, since
 * the session that produced these tokens is the one currently on the client.
 */
export function recordAccountFromSession(
  session: Session,
  profile?: Partial<StoredAccountProfile>,
): void {
  const user = session.user;
  if (!user || user.is_anonymous) return;
  if (!session.access_token || !session.refresh_token) return;

  const map = getStoredAccounts();
  const existing = map[user.id];
  map[user.id] = {
    userId: user.id,
    email: user.email ?? existing?.email ?? null,
    accessToken: session.access_token,
    refreshToken: session.refresh_token,
    profile: {
      // Prefer freshly-supplied profile fields, then the user metadata, then
      // whatever we had stored — never blank out a known name on a token refresh.
      name:
        profile?.name ??
        (typeof user.user_metadata?.full_name === 'string'
          ? user.user_metadata.full_name
          : null) ??
        existing?.profile.name ??
        null,
      avatar: profile?.avatar ?? existing?.profile.avatar ?? null,
    },
  };
  writeAccounts(map);
  setActiveUserId(user.id);
}

/** Patch the display snapshot for a stored account (e.g. once profile loads). */
export function updateStoredProfile(
  userId: string,
  profile: Partial<StoredAccountProfile>,
): void {
  const map = getStoredAccounts();
  const existing = map[userId];
  if (!existing) return;
  const next: StoredAccountProfile = {
    name: profile.name ?? existing.profile.name,
    avatar: profile.avatar ?? existing.profile.avatar,
  };
  // Avoid a pointless write (and storage event) when nothing changed.
  if (
    next.name === existing.profile.name &&
    next.avatar === existing.profile.avatar
  ) {
    return;
  }
  map[userId] = { ...existing, profile: next };
  writeAccounts(map);
}

/** Look up one stored account by id. */
export function getStoredAccount(userId: string): StoredAccount | undefined {
  return getStoredAccounts()[userId];
}

/**
 * Drop one account from the store. Returns the remaining accounts so the caller
 * can decide what to switch to next. Clears `activeUserId` if it pointed here.
 */
export function removeStoredAccount(userId: string): StoredAccount[] {
  const map = getStoredAccounts();
  delete map[userId];
  writeAccounts(map);
  if (getActiveUserId() === userId) setActiveUserId(null);
  return Object.values(map);
}

/** Wipe the whole multi-account store (sign out of all). */
export function clearStoredAccounts(): void {
  localStorage.removeItem(ACCOUNTS_KEY);
  localStorage.removeItem(ACTIVE_USER_ID_KEY);
}
